import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useQuickCreateStore, type QuickCreateAction, type QuickCreateItem } from './quickCreateStore';
import { isTopOverlay, pushOverlay, removeOverlay } from '../ui/overlayCore';
import { usePresence } from '../ui/useOverlay';
import { fabScrollStep, FAB_TUCK, speedDialDelay, speedDialDuration, speedDialKey, speedDialTone, SPEED_DIAL_MOTION, type FabScrollState } from './speedDialModel';

/**
 * The floating QuickCreate button and its speed-dial (owner request
 * 2026-10-06, supersedes Q-020b; DESIGN_SYSTEM.md §10.4).
 *
 * - One 56 px ink button at the bottom inline-end corner on every breakpoint
 *   (above the tab bar on phones). "+" turns 45° into "×" while open.
 * - The list unfolds upward: white pills with a tinted icon chip on the
 *   inline-start side, the first item (the owner's primary) on a filled ink
 *   chip, a light canvas scrim behind (no blur).
 * - WAI-ARIA menu button: `aria-expanded`, focus to the first item on open and
 *   back to the button on close, ↑/↓ follow the stack, Home/End, typeahead,
 *   Escape / Tab / outside press close. Staggered enter and exit (≤ 220 ms),
 *   cross-fade under reduced motion.
 * - Steps aside while a dialog or sheet is open (index.css), and the shell
 *   marks it `data-overlay-inert` so it is not reachable under a dialog.
 * - On phones and touch screens it also steps aside while the page scrolls
 *   down, so it never covers a row's button mid-scroll, and comes back on a
 *   scroll up or when the scroll stops (`useTuckOnScroll`, `fabScrollStep`).
 */
interface QuickCreateFabProps {
  items: QuickCreateItem[];
  onRun: (action: QuickCreateAction) => void;
  /**
   * Hidden while closed (a route whose own bottom bar it would cover). `N` and
   * the palette still open it; on close, focus returns to where it was.
   */
  concealed?: boolean;
}

/**
 * Whether the button should step aside for the current scroll (phones and touch
 * only, `FAB_TUCK.media`). Window scroll, read once per frame, passive listener.
 * Off while `active` is false (menu open, button concealed), and then it shows.
 */
function useTuckOnScroll(active: boolean): boolean {
  const [tucked, setTucked] = useState(false);
  useEffect(() => {
    if (!active || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(FAB_TUCK.media);
    let state: FabScrollState = { tucked: false, anchorY: window.scrollY };
    let raf = 0;
    let idle = 0;
    const apply = (next: FabScrollState) => {
      if (next.tucked !== state.tucked) setTucked(next.tucked);
      state = next;
    };
    const show = () => apply({ tucked: false, anchorY: window.scrollY });
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!mq.matches) { show(); return; }
        apply(fabScrollStep(state, window.scrollY));
        window.clearTimeout(idle);
        idle = window.setTimeout(show, FAB_TUCK.idleMs);
      });
    };
    const onMedia = () => { if (!mq.matches) show(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    mq.addEventListener?.('change', onMedia);
    return () => {
      window.removeEventListener('scroll', onScroll);
      mq.removeEventListener?.('change', onMedia);
      cancelAnimationFrame(raf);
      window.clearTimeout(idle);
      setTucked(false);
    };
  }, [active]);
  return active && tucked;
}

export default function QuickCreateFab({ items, onRun, concealed = false }: QuickCreateFabProps) {
  const { t } = useTranslation();
  const open = useQuickCreateStore((s) => s.open);
  const { mounted, state } = usePresence(open, speedDialDuration(items.length, 'exit') + 20);
  const fabRef = useRef<HTMLButtonElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [active, setActive] = useState(0);
  // What had focus before the list opened (used when the button is concealed).
  const openerRef = useRef<HTMLElement | null>(null);
  const concealedRef = useRef(concealed);
  concealedRef.current = concealed;
  const menuId = useId();
  const fabId = useId();
  const overlayId = useId();

  // The button is the anchor forms return focus to (runQuickCreate, palette).
  useEffect(() => {
    const el = fabRef.current;
    useQuickCreateStore.setState({ anchor: el });
    return () => { if (useQuickCreateStore.getState().anchor === el) useQuickCreateStore.setState({ anchor: null }); };
  }, []);

  const close = useCallback((refocus: boolean) => {
    useQuickCreateStore.getState().closeMenu();
    if (!refocus) return;
    const opener = openerRef.current;
    // Concealed: the button is about to hide, so go back to what had focus (or the page).
    const target = concealedRef.current
      ? (opener?.isConnected ? opener : document.getElementById('main'))
      : fabRef.current;
    target?.focus({ preventScroll: true });
  }, []);

  // Open: first item takes focus; outside press and Escape (topmost only) close.
  useEffect(() => {
    if (!open) return;
    pushOverlay(overlayId);
    setActive(0);
    const prev = document.activeElement as HTMLElement | null;
    openerRef.current = prev && prev !== document.body && !stackRef.current?.contains(prev) ? prev : null;
    const raf = requestAnimationFrame(() => itemRefs.current[0]?.focus({ preventScroll: true }));
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (stackRef.current?.contains(target) || fabRef.current?.contains(target)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || !isTopOverlay(overlayId)) return;
      e.preventDefault();
      close(true);
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      removeOverlay(overlayId);
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, overlayId, close]);

  const focusAt = (i: number) => {
    if (i < 0) return;
    setActive(i);
    itemRefs.current[i]?.focus({ preventScroll: true });
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const result = speedDialKey(e.key, active, items.map((i) => i.label));
    if (result.type === 'none') return;
    e.preventDefault();
    e.stopPropagation();
    if (result.type === 'focus') focusAt(result.index);
    else close(true);
  };

  const select = (action: QuickCreateAction) => {
    // Focus goes back to the button first, so the form it opens remembers the
    // button as its opener (same order as ui/Menu).
    close(true);
    requestAnimationFrame(() => onRun(action));
  };

  const toggle = () => {
    if (open) close(true);
    else useQuickCreateStore.getState().openMenu();
  };

  const phase = state === 'open' ? 'enter' : 'exit';
  // Never while the menu is open or closing, nor while concealed (already hidden).
  const tucked = useTuckOnScroll(!open && !mounted && !concealed);

  return (
    <div
      className="qc-fab-root"
      data-overlay-inert=""
      data-state={mounted ? state : 'closed'}
      data-concealed={concealed && !mounted ? '' : undefined}
      data-tucked={tucked ? '' : undefined}
    >
      {mounted && <div className="qc-scrim fixed inset-0 z-popover" data-state={state} aria-hidden="true" />}

      <div className={`qc-dock fixed flex flex-col items-end ${mounted ? 'z-popover' : 'z-nav'}`}>
        {mounted && (
          <div
            ref={stackRef}
            id={menuId}
            role="menu"
            aria-labelledby={fabId}
            aria-orientation="vertical"
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            className="qc-stack focus:outline-none"
            data-state={state}
          >
            {items.map((item, i) => {
              const Icon = item.icon;
              const style = {
                '--qc-delay': `${speedDialDelay(i, items.length, phase)}ms`,
                '--qc-dur': `${phase === 'enter' ? SPEED_DIAL_MOTION.itemIn : SPEED_DIAL_MOTION.itemOut}ms`,
              } as CSSProperties;
              return (
                <button
                  key={item.id}
                  ref={(el) => { itemRefs.current[i] = el; }}
                  type="button"
                  role="menuitem"
                  tabIndex={i === active ? 0 : -1}
                  onClick={() => select(item.id)}
                  onFocus={() => setActive(i)}
                  className="qc-item"
                  data-tone={speedDialTone(item.id, i)}
                  data-quick-create={item.id}
                  style={style}
                >
                  <span className="qc-chip" aria-hidden="true">
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1 text-start">
                    <span className={`block truncate text-sm text-text ${i === 0 ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    {item.hint && <span className="block truncate text-xs text-text-2">{item.hint}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <button
          ref={fabRef}
          id={fabId}
          type="button"
          onClick={toggle}
          onKeyDown={(e) => {
            if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !open) {
              e.preventDefault();
              useQuickCreateStore.getState().openMenu();
            }
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-keyshortcuts="N"
          aria-label={t('quickCreate.title')}
          data-tip={t('quickCreate.fabTip')}
          className="qc-fab"
          data-quick-create-trigger="fab"
        >
          {/* "+" (owner, 2026-10-11: it reads as "create", the OTE symbol did not); it turns 45° into "×" while open. */}
          <Plus size={26} strokeWidth={2} className="qc-fab-plus" aria-hidden="true" />
          {/* D-17 (clarity, C-09): a visible "חדש" from md up (a pill); phones keep the bare "+". */}
          <span className="qc-fab-label hidden md:inline" aria-hidden="true">{t('quickCreate.fabLabel')}</span>
        </button>
      </div>
    </div>
  );
}
